import {
	angleOf as angleOfVector,
	minus as subtractVector,
	dot as dotVector,
	lenOf as lenOfVector
} from 'vector-math-fp'

import {useRef,useEffect} from 'react'

//checks which side of the body a leg is facing
//best for seated persons with legs straight
//leg = left | right
export const getLegSide = (leg,keypoints)=>{
	const hip = keypoints[leg==='left'?11:12]
	const ankle = keypoints[leg==='left'?15:16]
	return (
		ankle.x > hip.x 
		? 'left'
		: 'right'
	)
}

export const getThighValgusAngle = (side,keypoints) => {
	const isLeft = side === 'left'
	const hip = keypoints[ isLeft ? 11 : 12 ]
	const knee = keypoints[ isLeft ? 13 : 14 ]

	//straight down
	const referenceVector = [0,-1]

	const thighVector = [hip.x - knee.x , hip.y - knee.y]

	const aVector = side === 'left' ? referenceVector : thighVector
	const bVector = side === 'left' ? thighVector : referenceVector

	return Math.acos(dotVector(aVector,bVector)/(vectorLength(aVector)*vectorLength(bVector)))/Math.PI*180
}

export const normalizeX = arr => {
	const min = Math.min(...arr.map(p=>p.x))
	const max = Math.max(...arr.map(p=>p.x))
	return arr.map(p=>({...p,x:(p.x-min)/(max-min)}))
}

export const isWebview = ()=> window.location.pathname.includes('fitswai')

//counts number of times hands came off hips and returns an array of integers
//eg [1,0,3] means first rep hands came off once, second rep no times, third rep 3 times
export const countPerRepHandsOffHips = (reps,data)=>{
	const repSlices = extractRepSlices(reps,data)
	const repHandsOffHipCounts = repSlices.map(s=>{
		let count = 0
		let handsOffHips = false
		s.forEach(d=>{
			if(!handsOffHips&&d.offHips){
				count += 1
				handsOffHips = true
			}
			if(handsOffHips&&!d.offHips){
				handsOffHips = false
			}
		})
		return count
	})
	return repHandsOffHipCounts
}

export const countTotalHandsOffHips = (height,log)=>{
	let count = 0
	let handsOffHips = false
	log.map(({keypoints})=>{
		const leftWrist = keypoints[9]
		const rightWrist = keypoints[10]
		const leftHip = keypoints[11]
		const rightHip = keypoints[12]
		const w = 0.12*height
		const h = 0.2*height

		const leftWristOutsideBox = (
			//outside of x bounds
			leftWrist.x <= leftHip.x-w-0.03*height ||
			leftWrist.x >= leftHip.x-0.03*height ||
			//outside of y bounds
			leftWrist.y <= leftHip.y-h ||
			leftWrist.y >= leftHip.y
		)

		const rightWristOutsideBox = (
			//outside of x bounds
			rightWrist.x <= (rightHip.x)+0.03*height ||
			rightWrist.x >= rightHip.x+w+0.03*height ||
			//outside of y bounds
			rightWrist.y <= rightHip.y-h ||
			rightWrist.y >= rightHip.y
		)
		return (leftWristOutsideBox||rightWristOutsideBox)
	}).forEach(offHips=>{
		if(!handsOffHips&&offHips){
			count += 1
			handsOffHips = true
		}
		if(handsOffHips&&!offHips){
			handsOffHips = false
		}
	})
	return count
}

export const singleLegSquatBalanceScore = (reps,data)=>{
	const repSlices = extractRepSlices(reps,data)
	const repHandsOffHipTimeTotals = repSlices.map(s=>{
		const totalTime = s[s.length-1].t - s[0].t
		let handsOffHipsTime = 0
		let handsOffHips = false
		let t0 = s[0].t
		s.forEach(d=>{
			if(!handsOffHips&&d.offHips){
				handsOffHips = true
				t0 = d.t
			}
			if(handsOffHips&&!d.offHips){
				handsOffHips = false
				handsOffHipsTime += d.t - t0
			}
		})
		return (1-handsOffHipsTime/totalTime)*100
	})
	const score = repHandsOffHipTimeTotals.reduce((t,s)=>t+s)/repHandsOffHipTimeTotals.length
	return score
}

export const roundDecimal = (v,precision=2) => (
	Math.round(v*Math.pow(10,precision))/Math.pow(10,precision)
)

export const measureShoulderDistance = keypoints => {
	const leftShoulder = keypoints[5]
	const rightShoulder = keypoints[6]
	return leftShoulder.x - rightShoulder.x
}

/**
 * returns which side of the body has the highest confidence
 * can be used as a proxy for detecting which side is facing the camera
 * Follows a cascade of methods to detect which side is facing forward
 * First checks if nose is between shoulders if nose is in view
**/
export const confidentSide = (keypoints)=>{
	const keypointsPerSide = 8
	//if delta between averages are not greater than this
	//value then the pose is considered "centered"
	const centeredDeltaThreshold = 0.05
	const leftScoreAverage = keypoints
		.filter(
			//is odd index and not the nose keypoint
			(_,i) => (i>0&&i%2===1)
		).reduce(
			(t,p) => t + p.score,
			0
		)/keypointsPerSide
	const rightScoreAverage = keypoints
		.filter(
			//is even index and not the nose keypoint
			(_,i) => (i>0&&i%2===0)
		).reduce(
			(t,p) => t + p.score,
			0
		)/keypointsPerSide

	const delta = Math.abs(leftScoreAverage - rightScoreAverage)

	if (delta <= centeredDeltaThreshold) return 'front'
	return leftScoreAverage > rightScoreAverage ? 'left' : 'right'
}

/**
	data -- array of a data to segment
	key -- what key on the data should be used when checking for healthiness
	isHealthy -- a predicate function to check if a data value is health
**/
export const segmentDataByHealthiness = (data,key,isHealthy)=>{
	const result = []
	const d0 = data[0]
	let status = isHealthy(d0[key])
	const generateRange = (healthy,t1=0,v1=0,i1=0,logIdx1=0,t2=0,v2=0,i2=0,logIdx2=0)=>({
		healthy,
		logIdx1,logIdx2,
		//indexes
		i1,i2,
		//times
		t1,t2,
		//key values at those times
		v1,v2
	})
	let range = generateRange(status,d0.t,d0[key],0,0)
	data.forEach((d,i)=>{
		const dStatus = isHealthy(d[key])
		if (dStatus!==status) {
			status = dStatus
			const dPrev = data[i-1]
			range.t2 = dPrev.t
			range.v2 = dPrev[key]
			range.i2 = i
			range.logIdx2 = dPrev.logIdx
			result.push(range)
			range = generateRange(dStatus,d.t,d[key],i,d.logIdx)
		}
	})
	//"close" the final range
	if(data.length>0){
		const dLast = data[data.length-1]
		range.t2 = dLast.t
		range.v2 = dLast[key]
		range.i2 = data.length-1
		range.logIdx2 = dLast.logIdx
		result.push(range)
	}
	return result
}

const vectorLength = (vector)=>(
	Math.sqrt(
		vector.reduce(
			(t,n) => t + Math.pow(n,2),
			0
		)
	)
)

const arrMode = (arr)=>{
	const items = {
	}
	arr.forEach(side=>{
		if(!items[side]){
			items[side] = 0
		}
		items[side] += 1
	})
	const max = Math.max(...Object.values(items))
	for(const side in items){
		const value  = items[side]
		if(value===max) return side
	}
}

/**
	Side data is noisy (eg sometimes it will predict that person is facing forward when they are not)
	to smooth out the noise we take a radius r around each point update it's value to be the mode of
	all values within the radius around that point
**/
export const smooth = (arr,r)=>{
	const result = [];
	arr.forEach((item,i)=>{
		const startIdx = Math.max(i-r,0)
		const endIdx = Math.min(arr.length,i+r+1)
		const slice = arr.slice(startIdx,endIdx)
		result.push(arrMode(slice))
	})
	return result
}

export const interpolateMidpoints = (keypointLog,numPasses=1) => {
	if(numPasses===0) return keypointLog
	const result = []
	for (var i = 0; i < keypointLog.length; i++) {
		const {keypoints,t:t1} = keypointLog[i]
		result.push(keypointLog[i])
		if(i<keypointLog.length-1){
			//interpolate a linear point between the current point and the next
			const {keypoints:nextKeypoints,t:t2} = keypointLog[i+1]
			const interpolatedKeypoints = []
			for (var j = 0; j < keypoints.length; j++) {
				const p1 = keypoints[j]
				const p2 = nextKeypoints[j]
				interpolatedKeypoints.push({
					x:(p1.x + p2.x)/2,
					y:(p1.y + p2.y)/2,
					score:(p1.score + p2.score)/2,
				})
			}
			result.push({
				keypoints:interpolatedKeypoints,
				t:(t1 + t2)/2
			})
		}
	}
	return interpolateMidpoints(result,numPasses-1)
}

// requires side view
export const sideKneeAngle = (side,keypoints)=>{
	//get keypoints
	const hip = keypoints[ side === 'left' ? 11 : 12 ]
	const knee = keypoints[ side === 'left' ? 13 : 14 ]
	const ankle = keypoints[ side === 'left' ? 15 : 16 ]

	//convert to vectors centered on hip
	const thighVector = [hip.x - knee.x , hip.y - knee.y]
	const calfVector = [ankle.x - knee.x , ankle.y - knee.y]

	const aVector = side === 'left' ? calfVector : thighVector
	const bVector = side === 'left' ? thighVector : calfVector

	return Math.acos(dotVector(aVector,bVector)/(vectorLength(aVector)*vectorLength(bVector)))/Math.PI*180
}

export const frontOverheadShoulderAngle = (side,keypoints) => {
	const referenceVector = [0,1]
	//get keypoints
	const shoulder = keypoints[ side === 'left' ? 5 : 6 ]
	const elbow = keypoints[ side === 'left' ? 7 : 8 ]

	//create shoulder/elbow vector
	const armVector = [
		elbow.x - shoulder.x ,
		shoulder.y - elbow.y
	]

	const aVector = side === 'left' ? referenceVector : armVector
	const bVector = side === 'left' ? armVector : referenceVector

	return Math.acos(dotVector(aVector,bVector)/(vectorLength(aVector)*vectorLength(bVector)))/Math.PI*180
}

export const sideShoulderAngle = (side,keypoints)=>{
	//get keypoints
	const shoulder = keypoints[ side === 'left' ? 5 : 6 ]
	const elbow = keypoints[ side === 'left' ? 7 : 8 ]
	const hip = keypoints[ side === 'left' ? 11 : 12]

	//create shoulder/elbow vector
	const armVector = [
		elbow.x - shoulder.x ,
		elbow.y - shoulder.y
	]

	const trunkVector = [
		hip.x - shoulder.x,
		hip.y - shoulder.y
	]


	return Math.acos(dotVector(trunkVector,armVector)/(vectorLength(trunkVector)*vectorLength(armVector)))/Math.PI*180
}

export const repConsistency = (reps) => {
	const diffs = reps.ended.map((e,i)=>e-reps.started[i])
	if(!diffs.length){
		return 0
	}
	const sum = diffs.reduce((acc, val) => acc + val)
	const { length: num } = diffs
	const median = sum / num
	let variance = 0
	diffs.forEach(num => {
		variance += ((num - median) * (num - median))
	})
	variance /= num
	return Math.min(100-Math.sqrt(variance)/median*100,100)
}

export const tempoConsistency = (reps) => (
	Math.min((repConsistency(reps)+restConsistency(reps))/2,100)
)

export const repKneeSymmetryScore = (reps,poseLog,height)=>{
	//extract slices
	const slices = extractRepSlices(reps,poseLog)
	//map slices to knee symmetry data 
	const repKneeSymmetrySlices = slices.map(repSlice=>{
		const leftValgusData = repSlice.map(pose=>{
			const toeOffset = 1.75/69*height
			const knee = pose.keypoints[13]
			const ankle = pose.keypoints[15]
			const toeX = ankle.x - toeOffset
			const inches = (toeX - knee.x)/height*69
			const angle = getThighValgusAngle('left',pose.keypoints)
			return {valgus:{inches,angle},t:pose.t}
		})//end repslice.map	

		const rightValgusData = repSlice.map(pose=>{
			const toeOffset = 1.75/69*height
			const knee = pose.keypoints[14]
			const ankle = pose.keypoints[16]
			const toeX = ankle.x + toeOffset
			const inches = (knee.x - toeX)/height*69
			const angle = getThighValgusAngle('right',pose.keypoints)
			return {valgus:{inches,angle},t:pose.t}
		})//end repslice.map

		const kneeSymmetryDifferences = leftValgusData.map((leftValgus,i)=>(
			Math.abs(leftValgus.valgus.inches - rightValgusData[i].valgus.inches)
		))

		return kneeSymmetryDifferences
	})
	//merge all the arrays
	const repKneeSymmetries = [].concat(...repKneeSymmetrySlices)

	const healthySymmetries = repKneeSymmetries.filter(s=>s<=1)
	return Math.min(healthySymmetries.length/repKneeSymmetries.length*100,100)
}

export const maxDepthScore = (maxDepthPercent,height,femurLength)=>(
	Math.min(maxDepthPercent/(femurLength/height*.93),100)
)

export const repsTrunkCollapseScore = (reps,poseLog)=>{
	const slices = extractRepSlices(reps,poseLog)
	const trunkCollapseSlices = slices.map(repSlice=>{
		const torsoAngles = repSlice.map(
			pose => (
				{
					angle:torsoSideAngle(pose.keypoints,'right'),
					t:pose.t
				}
			)
		)

		//get calf angles
		const calfAngles = repSlice.map(
			pose => (
				{
					angle:calfSideAngle(pose.keypoints,'right'),
					t:pose.t
				}
			)
		)

		//trunk/calf angle diff
		const trunkCalfAngleDiffs = calfAngles.map((calfAngle,i)=>(
			calfAngle.angle - torsoAngles[i].angle
		))

		return trunkCalfAngleDiffs
	})

	const trunkCollapseData = [].concat(...trunkCollapseSlices)
	const healthyData = trunkCollapseData.filter(d=>d<=10)
	return Math.min(healthyData.length/trunkCollapseData.length*100,100)
}

export const repsHipShiftScore = (reps,positionData)=>{
	//extract rep slices
	const positionSlices = extractRepSlices(reps,positionData)
	//merge the slices into a single array for averaging
	const repPositionData = [].concat(...positionSlices)
	const onCenterData = repPositionData.filter( d => d.status === 'center' )
	return Math.min(onCenterData.length/repPositionData.length*100,100)
}

export const repsValgusScore = (reps,poseLog,side)=> {
	//extract rep slices
	const valgusSlices = extractRepSlices(reps,poseLog).map(
		slice => slice.map( pose => {
			return getThighValgusAngle(side,pose.keypoints)
		})//end repslice.map
	)
	//merge the slices into a single array for averaging
	const repValgusData = [].concat(...valgusSlices)
	const inVarusData = repValgusData.filter( d => d >= 0 )
	return Math.min(inVarusData.length/repValgusData.length*100,100)
}

export const depthScore = (reps,chestPosition,bottomY,topY)=>{
	//generate map from timestamp to knee position idx
	const chestTimeIdxMap = {}
	chestPosition.forEach((p,i)=>{
		chestTimeIdxMap[p.t] = i
	})
	//extract position data during reps
	const positionSlices = []
	for (var i = 0; i < reps.started.length; i++) {
		const startIdx = chestTimeIdxMap[reps.started[i]]
		const endIdx = chestTimeIdxMap[reps.ended[i]]
		positionSlices.push(chestPosition.slice(startIdx,endIdx))
	}
	//console.log(positionSlices)
	//for each position slice get the max depth
	const sliceMaxDepths = positionSlices.map(s=>Math.max(...s.map(p=>p.y)))
	const avgDepth = sliceMaxDepths.reduce((t,d)=>t+d,0)/sliceMaxDepths.length
	const score = Math.max(Math.min((avgDepth-topY)/(bottomY-topY),1)*100,0)
	//console.log(chestPosition)
	//console.log(avgDepth,topY,bottomY)
	return score
}

export const depthScoreByKneeAngle = (reps,kneeAngles,maxScoreAngle=30)=>{
	const maxValues = extractRepSlices(reps,kneeAngles)
		.map( d => Math.max( ...d.map( d => d.angle ) ) )
	const perValueScores = maxValues.map(v=>(
		Math.min(maxScoreAngle,v)/maxScoreAngle
	))
	const score = perValueScores.reduce((t,a)=>t+a,0)/perValueScores.length
	return score*100
}

export const extractRepSlices = (reps,series)=>{
	//generate map from timestamp to knee position idx
	const timeIdxMap = {}
	series.forEach((p,i)=>{
		timeIdxMap[p.t] = i
	})
	//extract position data during reps
	const positionSlices = reps.started.slice(0,reps.ended.length).map((start,i)=>{
		const end = reps.ended[i]
		return series.filter(p=>p.t>=start && p.t<=end)
	})
	return positionSlices
}

export const nonMonotonicIndexes = (arr,decreasing=true)=>{
	const indexes = []
	if ( arr.length < 3 ) return indexes
	for (var i = 2; i < arr.length; i++) {
		if(decreasing&&arr[i]>arr[i-1]&&arr[i-1]>arr[i-2]){
			indexes.push(i)
			indexes.push(i-1)
			indexes.push(i-2)
		}
		if(!decreasing&&arr[i]<arr[i-1]&&arr[i-1]<arr[i-2]){
			indexes.push(i)
			indexes.push(i-1)
			indexes.push(i-2)
		}
	}	
	return indexes
}

export const torsoSideAngle = (keypoints,side) => {
	const isLeft = side==='left'
	const shoulder = keypoints[isLeft?5:6]
	const hip = keypoints[isLeft?11:12]
	const torsoVector = [
		hip.x - shoulder.x,
		hip.y - shoulder.y
	]
	return (
		isLeft
		? angleOfVector(torsoVector)
		: 180 - angleOfVector(torsoVector)
	)
}

export const kneeForwardFlexion = (keypoints,side,height) => {
	const isLeft = side === 'left'
	const knee = keypoints[isLeft?13:14]
	const ankle = keypoints[isLeft?15:16]
	return Math.abs(knee.x - ankle.x)/height*69
}

export const calfSideAngle = (keypoints,side) => {
	const isLeft = side==='left'
	const knee = keypoints[isLeft?13:14]
	const ankle = keypoints[isLeft?15:16]
	const calfVector = [
		ankle.x - knee.x,
		ankle.y - knee.y
	]
	return (
		isLeft
		? angleOfVector(calfVector)
		: 180 - angleOfVector(calfVector)
	)
}

export const frontHipShift = (keypoints,height)=>{
	const hipX = hipPosition(keypoints).x
	const leftAnkle = keypoints[15]
	const rightAnkle = keypoints[16]
	const squatCenter = (leftAnkle.x + rightAnkle.x)/2
	return (hipX - squatCenter)/height*69
}

export const argMin = arr => arr.indexOf(Math.min(...arr))

export const argMax = arr => arr.indexOf(Math.max(...arr))

/**
 * outputs a map from timestamp to if the knee was caving during this time
 */
export const getKneeCaveTimeMap = (reps,kneePosition,hipPosition)=>{
	//partition into slices
	const kneeSlices = extractRepSlices(reps,kneePosition)
	const hipSlices = extractRepSlices(reps,hipPosition)
	const map = {}
	const segmentedKneeSlices = kneeSlices.map( (kneeSlice,i) => {
		//use the chest position to determine when movement concavity
		//changed (ie shifted from going down to going up)
		const hipSlice = hipSlices[i]
		const hipArgMax = argMax(hipSlice.map(p=>p.y))
		const down = kneeSlice.slice(0,hipArgMax)
		const up = kneeSlice.slice(hipArgMax)

		const downCaves = nonMonotonicIndexes(down.map(p=>p.x),false)
		const upCaves = nonMonotonicIndexes(up.map(p=>p.x))

		down.forEach((d,i)=>{ map[d.t] = downCaves.includes(i) })
		up.forEach((d,i)=>{ map[d.t] = upCaves.includes(i) })
	})
	
	return map
}

export const restConsistency = (reps) => {
	const diffs = reps.started.slice(1).map((s,i)=>s-reps.ended[i])
	if (!diffs.length) {
		return 0
	}
	const sum = diffs.reduce((acc, val) => acc + val)
	const { length: num } = diffs
	const median = sum / num
	let variance = 0
	diffs.forEach(num => {
		variance += ((num - median) * (num - median))
	})
	variance /= num
	return Math.min(100-Math.sqrt(variance)/median*100,100)
}

/**
 * Takes average of all points
 * Then calculates the average absolute percent deviation
 * Then averages all percent deviations
 */
export const kneeArticulationConsistency = (reps,kneePosition,kneeCaveMap)=>{
	//generate map from timestamp to knee position idx
	const kneeTimeIdxMap = {}
	kneePosition.forEach((p,i)=>{
		kneeTimeIdxMap[p.t] = i
	})
	//extract position data during reps
	const positionSlices = []
	for (var i = 0; i < reps.started.length; i++) {
		const startIdx = kneeTimeIdxMap[reps.started[i]]
		const endIdx = kneeTimeIdxMap[reps.ended[i]]
		positionSlices.push(kneePosition.slice(startIdx,endIdx))
	}
	//get the minimum array size for all the slices
	const minSliceLength = Math.min(...positionSlices.map(ps=>ps.length))
	const averages = []
	for (var i = 0; i < minSliceLength; i++) {
		let total = 0
		for (var j = 0; j < positionSlices.length; j++) {
			total += normalizeX(positionSlices[j])[i].x
		}
		averages.push(total/positionSlices.length)
	}
	const deviations = []
	for (var i = 0; i < minSliceLength; i++) {
		let total = 0
		for (var j = 0; j < positionSlices.length; j++) {
			const avg = averages[i]
			total += Math.abs((normalizeX(positionSlices[j])[i].x-avg)/avg,2)
		}
		deviations.push(total/positionSlices.length)
	}
	const caveBools = Object.values(kneeCaveMap)
	const stabilityScore = caveBools.reduce((carry,bool)=>carry+!bool,0)/caveBools.length
	return Math.min(100*(1-(deviations.reduce((carry,dev)=>carry+dev,0)/minSliceLength))*stabilityScore,100)
}

/**
 * Very similar to kneeArticulationConsistency but uses valgus/varus
 * data instead of the absolute deviation from the center
 */
export const singleLegKneeScore = (reps,data)=>{
	const repSlices = extractRepSlices(reps,data)
	const repsValgusTimes = repSlices.map(s=>{
		const totalTime = s[s.length-1].t - s[0].t
		let valgusTime = 0
		let inValgus = false
		let t0 = s[0].t
		s.forEach(d=>{
			if(!inValgus&&d.valgus>0){
				inValgus = true
				t0 = d.t
			}
			if(inValgus&&d.valgus<=0){
				inValgus = false
				valgusTime += d.t - t0
			}
		})
		return (1-valgusTime/totalTime)*100
	})
	const score = repsValgusTimes.reduce((t,s)=>t+s)/repsValgusTimes.length
	return score
}

export const arrAvg = arr => arr.reduce((t,n)=>t+n,0)/arr.length

export const appendAvg = arr => [...arr,arrAvg(arr)]

/**
 * Start tolerance is a little bit of buffer we add above a base line (eg where the nose is)
 * so that any jitter in predictions while a person is standing doesn't cause erroneous readouts
 */
export const repsMaxHeightDifference = (reps,series,baseLine,poseHeight,heightInches=69,startTolerance=0.03)=>(
	extractRepSlices(reps,series)
		.map(s => s.map(p=>p.y))
		.map(s => Math.min(...s))
		.map(y => baseLine + startTolerance*poseHeight - y)
		//.map(d => d/poseHeight)
		.map(d => d/poseHeight*heightInches)
)

export const centerLineConsistency = data => {
	const onCenter = data.reduce(
		(carry,d)=>carry + (d.status==='center'?1:0),
		0
	)
	return Math.min(onCenter/data.length*100,100)
}

export const repsCenterLineScore = (reps,data) => {
	//map each slice to an array of just the x values
	const repSlices = extractRepSlices(reps,data).map(s=>s.map(d=>d.x))
	//map each slice to a score for that rep
	const repCenterScores = repSlices.map(slice=>{
		//get average
		const avg = slice.reduce((t,x)=>t+x,0)/slice.length
		//calculate absolute differences around average
		const deltas = slice.map(x=>Math.abs(x-avg))
		const avgDelta = deltas.reduce((t,d)=>t+d,0)/deltas.length
		const repScore = 1 - avgDelta/8
		return repScore
	})
	const avgRepCenterScore = repCenterScores.reduce((t,s)=>t+s,0)/repCenterScores.length
	return Math.min(avgRepCenterScore*100,100)
}

export const chestCenter = (keypoints,key='x')=>(
	(keypoints[5][key] + keypoints[6][key])/2
)

export const chestPosition = (keypoints)=>(
	{
		x:(keypoints[5].x + keypoints[6].x)/2,
		y:(keypoints[5].y + keypoints[6].y)/2,
	}
)

export const hipPosition = (keypoints)=>(
	{
		x:(keypoints[11].x + keypoints[12].x)/2,
		y:(keypoints[11].y + keypoints[12].y)/2,
	}
)

export const centerPosition = (keypoints)=>(
	{
		x:((keypoints[5].x + keypoints[6].x)/2 + (keypoints[11].x + keypoints[12].x)/2)/2,
		y:((keypoints[5].y + keypoints[6].y)/2 + (keypoints[11].y + keypoints[12].y)/2)/2,
	}
)

export const hipCenter = (keypoints)=>(
	(keypoints[11].x + keypoints[12].x)/2
)

export const useAnimationFrame = callback => {
	// Use useRef for mutable variables that we want to persist
	// without triggering a re-render on their change
	const requestRef = useRef()
	const previousTimeRef = useRef()

	const animate = time => {
		if (previousTimeRef.current != undefined) {
			const deltaTime = time - previousTimeRef.current;
			callback(deltaTime)
		}
		previousTimeRef.current = time;
		requestRef.current = requestAnimationFrame(animate);
	}

	const start = ()=>{
		requestRef.current = requestAnimationFrame(animate)
	}

	const cancel = ()=>{
		//console.log('canceled')
		cancelAnimationFrame(requestRef.current)
	}

	useEffect(() => {
		return cancel
	}, [callback])

	return {cancel,start}
}

export const poseFemurLength = (keypoints,side='avg') => {
	const leftHip = keypoints[11]
	const leftKnee = keypoints[13]
	const leftFemurLength = Math.sqrt(
		Math.pow(leftHip.x - leftKnee.x,2)
		+
		Math.pow(leftHip.y - leftKnee.y,2)
	)
	if(side === 'left') return leftFemurLength

	const rightHip = keypoints[12]
	const rightKnee = keypoints[14]

	const rightFemurLength = Math.sqrt(
		Math.pow(rightHip.x - rightKnee.x,2)
		+
		Math.pow(rightHip.y - rightKnee.y,2)
	)

	if(side==='right') return rightFemurLength

	return (leftFemurLength+rightFemurLength)/2
}

/**
 * how is height esitmated:
 * Assume that person is average height (~69 inchese)
 * Assume that person neck/head height is ~13 inches
 * This makes the percent of height of head/neck 13/69
 * Calculate using the chest center y and the most distal
 * ankle + the estimated top of head
 * If a person is not facing the camere then the head keypoints 
 * are not very accurate but shoulders are
**/
export const poseHeight = keypoints => {
	//this offset value is to account for the fact
	//that chest chenter is a few inches under
	//the neck
	const offset = 4
	const shoulderToHead = 13
	const heightInches = 67
	const chest = chestPosition(keypoints)
	const leftAnkle = keypoints[15]
	const rightAnkle = keypoints[16]
	const ankleY = Math.max(leftAnkle.y,rightAnkle.y)
	const chestAnkleDelta = ankleY - chest.y
	const height = (
		chestAnkleDelta
		/
		( 1 - ( shoulderToHead + offset ) / heightInches )
	)
	return height
}

export const balancingSide = (keypoints)=>{
	//if one ankle is this far above the other then it is considered lifted
	const liftTolerance = poseHeight(keypoints)*0.07
	/*
	resultsRef.current.chestPosition.push({
		x:320-chestCenter(keypoints,'x'),
		y:chestCenter(keypoints,'y'),
		status:calculateCenterStatus(320-chestCenter(keypoints),guidesRef.current),
		t
	})*/
	const leftAnkle = keypoints[15]

	const rightAnkle = keypoints[16]

	const leftAnkleLifted = leftAnkle.y <= rightAnkle.y - liftTolerance
	const rightAnkleLifted = rightAnkle.y <= leftAnkle.y - liftTolerance
	if(leftAnkleLifted){
		return 'right'
	}else if(rightAnkleLifted) {
		return 'left'
	}else {
		return 'none'
	}
}

export const calculateCenterStatus = (center,guides)=>{
	if(center>guides.center.left) return 'left'
	if(center<guides.center.right) return 'right'
	return 'center'
}

export const calculateShoulderTilt = (keypoints)=>{
	const leftShoulder = keypoints[5]
	const rightShoulder = keypoints[6]
	//rightmost shoulder relative to the axis against
	//which we measure the angle
	const rightmostShoulder = (
		leftShoulder.x < rightShoulder.x 
		? rightShoulder
		: leftShoulder
	)
	//begin vector math for calculating the angle
	const rightmostVector = [rightmostShoulder.x,rightmostShoulder.y]
	const midPointVector = [
		(leftShoulder.x + rightShoulder.x)/2,
		(leftShoulder.y + rightShoulder.y)/2
	]
	const angledVector = subtractVector(midPointVector,rightmostVector)
	return angleOfVector(angledVector)
}

export const calculateHipTilt = (keypoints)=>{
	const leftHip = keypoints[11]
	const rightHip = keypoints[12]
	//rightmost shoulder relative to the axis against
	//which we measure the angle
	const rightmostShoulder = (
		leftHip.x < rightHip.x 
		? rightHip
		: leftHip
	)
	//begin vector math for calculating the angle
	const rightmostVector = [rightmostShoulder.x,rightmostShoulder.y]
	const midPointVector = [
		(leftHip.x + rightHip.x)/2,
		(leftHip.y + rightHip.y)/2
	]
	const angledVector = subtractVector(midPointVector,rightmostVector)
	return angleOfVector(angledVector)
}

export const tiltScore = (data)=>{

	const total = data.reduce(
		(carry,item)=>carry+Math.abs(item.y),
		0
	)

	const avg = (
		data.length > 0
		? total/data.length
		: 0
	)

	const normalizedScore = 1 - avg/90

	const score = normalizedScore*100
	return score
}

export const kneeHeightScore = (data,ideal,bottomY)=>{
	if (data.length === 0) return 0
	//ignore first 300ms of datapoints to prevent penalizing the score
	//while the leg is being lifted
	const t0 = data[0].t
	//only keep those with total elapsed time since start
	//greater than 300 ms (ie those beyond the 300ms mark)
	data = data.filter( d => d.t - t0 > 300 )

	//

	const totalAboveIdeal = data.reduce(
		(carry,item) => carry + (item.y < ideal ? 1 : (bottomY - item.y)/(bottomY - ideal)) ,
		0
	)
	const avg = (
		data.length > 0 
		? totalAboveIdeal/data.length
		: 0
	)

	const score = avg*100
	return score
}

/**
 * given a thigh/calf length and a knee bend angle
 * estimates the distnace beetween heel and butt by
 * using the distance between hip and ankle keypoints
 * offset by a small amount proportional to leg length
 * Then creates a percent value and multiplies by the 
 * average leg length in inches
 */
export const heelHipDistanceEstimate = (thighLength,calfLength,angle)=>{
	return Math.sqrt(
		Math.pow(thighLength,2) +
		Math.pow(calfLength,2) +
		2*thighLength*calfLength*Math.cos((180 - angle)/180*Math.PI)
	)/(thighLength+calfLength)*31 - 0.05*31
}

export const calculateThighLength = (keypoints,side)=>{
	const isLeft = side === 'left'
	const hip = keypoints[isLeft?11:12]
	const knee = keypoints[isLeft?13:14]

	const hipVector = [hip.x,hip.y]
	const kneeVector = [knee.x,knee.y]

	return lenOfVector(subtractVector(kneeVector,hipVector))
}

//projects all keypoints unto a specific axis
export const projectKeypointsUntoAxis = (keypoints,axis='x')=>{
	//this is the field to set all values to 0
	//setting all values to zero for this axis results in a projection
	const zeroAxis = axis === 'x' ? 'y' : 'x'

	return keypoints.map(
		p=>({
			...p,
			[zeroAxis]:0
		})
	)
}

export const calculateCalfLength = (keypoints,side)=>{
	const isLeft = side === 'left'
	const knee = keypoints[isLeft?13:14]
	const ankle = keypoints[isLeft?15:16]

	const kneeVector = [knee.x,knee.y]
	const ankleVector = [ankle.x,ankle.y]

	return lenOfVector(subtractVector(ankleVector,kneeVector))
}

export const calculateForearmLength = (side,keypoints)=>{
	const isLeft = side === 'left'
	const elbow = keypoints[isLeft?7:8]
	const wrist = keypoints[isLeft?9:10]

	const elbowVector = [elbow.x,elbow.y]
	const wristVector = [wrist.x,wrist.y]

	return lenOfVector(subtractVector(wristVector,elbowVector))
}

export const calculateUpperArmLength = (side,keypoints)=>{
	const isLeft = side === 'left'
	const elbow = keypoints[isLeft?7:8]
	const shoulder = keypoints[isLeft?5:6]

	const elbowVector = [elbow.x,elbow.y]
	const shoulderVector = [shoulder.x,shoulder.y]

	return lenOfVector(subtractVector(shoulderVector,elbowVector))
}

export const calculateArmLength = (side,keypoints)=>(
	calculateUpperArmLength(side,keypoints)
	+
	calculateForearmLength(side,keypoints)
)

export const nearest = (v,stepSize) => parseInt(stepSize*Math.ceil(v/stepSize))